import { generateTicks, scanTicks, type Band, type TickInformation } from './core';
import {
  convertAmericanToDecimal,
  convertImpliedProbabilityToAmerican,
} from '@/core/utils';

const bands: Band[] = [
  [1.01, 1.95, 0.01],
  // Finer 0.005 step around even money so decimal users can match the
  // half-step American ladder (e.g. 1.995 ↔ -100.5, 2.005 ↔ +100.5).
  [1.95, 2.05, 0.005],
  [2.05, 3, 0.01],
  [3, 4, 0.05],
  [4, 6, 0.1],
  [6, 10, 0.2],
  // Above 10 has ticks of 0.5 handled by getDecimalTickInfoAbove10
];

const ticks = generateTicks(bands, 3);

function preprocessDecimal(raw: number): number {
  // Negatives treated as american
  if (raw < -100) return convertAmericanToDecimal(raw);
  // Weird dones treated as 2
  if (raw < 0) return 2;
  // 0 to 1 as implied percentage
  if (raw < 1) {
    const ip = raw * 100;
    const american = convertImpliedProbabilityToAmerican(ip);
    return convertAmericanToDecimal(american);
  }
  if (raw < 1.01) return 1.01;
  return raw
}

function getDecimalTickInfoAbove10(value: number): TickInformation {
  const step = 0.5;
  const remainder = ((value - 10) % step + step) % step;
  if (remainder < 0.001) {
    return { prev: value - step, snap: value, next: value + step };
  }
  const prev = value - remainder;
  const next = prev + step;
  const snap = remainder < step / 2 ? prev : next;
  return { prev, snap, next };
}

export function getDecimalTickInfo(rawValue: number): TickInformation {
  const value = preprocessDecimal(rawValue);
  if (value >= 10) return getDecimalTickInfoAbove10(value);
  return scanTicks(value, ticks);
}
